import "../global";
import { PageAttributesExtractor } from "../shared/PageAttributesExtractor";
import { PageDebugInfo } from "../shared/PageDebugInfo";
import { DotCaPageAttributes } from "./DotCaPageAttributes";
import { StreamMediaType } from "../shared/StreamMediaType";
import { getSanitizedDataLayerValue } from "../shared/DataLayerHelper";
import { GeoIpService } from "../shared/geoLocation/GeoIpService";
import { inject, injectable, singleton } from "tsyringe";

@singleton()
@injectable()
export class DotCaPageAttributesExtractor implements PageAttributesExtractor {
  constructor(@inject(GeoIpService) private geoIpService: GeoIpService) {}

  /**
   * Extract page attributes for Rc_Read and Rc_Loaded Event
   */
  public extractAttributes(): DotCaPageAttributes {
    return {
      application: this.getAttribute("page.Application", "rc.application"),
      codeEmission: this.getAttribute("page.CodeEmission", "rc.codeEmission"),
      dossier: this.getAttribute("page.Dossier", "rc.dossier"),
      emission: this.getAttribute("page.Emission", "rc.emission"),
      format: this.getAttribute("page.Format", "rc.format"),
      groupeSection: this.getAttribute("page.GroupeSection", "rc.groupeSection"),
      pageSegment: this.getAttribute("page.PageSegment", "rc.pageSegment"),
      section: this.getAttribute("page.Section", "rc.section"),
      segment: this.getAttribute("page.Segment", "rc.segment"),
      sousThemes: this.getSousThemes(),
      theme: this.getAttribute("page.Theme", "rc.theme"),
      typeContenu: this.getAttribute("page.TypeContenu", "rc.typeContenu"),
    };
  }

  extractPageLoadedAttributes(): DotCaPageAttributes {
    const attributes = this.extractAttributes();
    attributes.reseau = this.getAttribute("page.Reseau", "rc.reseau");
    return attributes;
  }

  extractReadAttributes(): DotCaPageAttributes {
    const attributes = this.extractAttributes();
    attributes.reseau = this.getAttribute("page.Reseau", "rc.reseau");
    return attributes;
  }

  extractDebugInfo(): PageDebugInfo {
    return {
      pageAppId: dataLayerHelper.get<string>("page.Application"),
      pageVersion: dataLayerHelper.get<string>("page.VersionProjet"),
    };
  }

  /**
   * Extract page attributes for Rc_Streamed_Audio and Rc_Streamed_Video
   * @param contentType Audio or video
   */
  extractStreamAttributes(contentType: StreamMediaType): DotCaPageAttributes {
    const attributes = {
      application: this.getAttribute("page.Application", "rc.application"),
      codeEmission: this.getAttribute("page.CodeEmission", "rc.codeEmission"),
      emission: this.getAttribute("page.Emission", "rc.emission"),
      format: this.getAttribute("page.Format", "rc.format"),
      groupeSection: this.getAttribute("page.GroupeSection", "rc.groupeSection"),
      section: this.getAttribute("page.Section", "rc.section"),
      segment: this.getAttribute("page.Segment", "rc.segment"),
      sousThemes: this.getSousThemes(),
      theme: this.getAttribute("page.Theme", "rc.theme"),
      reseau: this.getAttribute("page.Reseau", "rc.reseau"),
    } as DotCaPageAttributes;
    if (contentType === StreamMediaType.Audio) {
      attributes.audience = getSanitizedDataLayerValue("media.Audience");
    } else if (contentType === StreamMediaType.Video) {
      attributes.dossier = this.getAttribute("page.Dossier", "rc.dossier");
      attributes.pageSegment = this.getAttribute("page.PageSegment", "rc.pageSegment");
      attributes.typeContenu = this.getAttribute("page.TypeContenu", "rc.typeContenu");
    }
    return attributes;
  }

  async extractStartSessionAttributes(): Promise<DotCaPageAttributes> {
    const attributes = this.extractAttributes();
    const postalCode = await this.geoIpService.fetchAndStoreUserPostalCode();
    if (postalCode) {
      attributes.postalCode = postalCode;
      attributes.fsa = postalCode.substring(0, 3);
    } else {
      attributes.fsa = getSanitizedDataLayerValue("user.fsa");
    }

    return attributes;
  }

  /**
   * Get the datalayer value OR the meta value for old v3.5 front-end websites
   */
  private getAttribute(dataLayerVariableName: string, rcMetaName: string): string | undefined {
    const dlValue = dataLayerHelper.get(dataLayerVariableName) as string;

    // Some "vintage" code may set the 'null' string as the value instead of null or undefined.
    if (dlValue && dlValue !== "null") {
      return dlValue;
    }

    if (rcMetaName) {
      const metaValue = this.getMetaData(rcMetaName);
      if (metaValue && metaValue !== "null") {
        return metaValue;
      }
    }

    return undefined;
  }

  private getMetaData(metaTagName: string): string | undefined {
    let metaTagElements = document.getElementsByName(metaTagName) as NodeListOf<HTMLMetaElement>;
    if (metaTagElements.length > 0 && metaTagElements[0].content) {
      return metaTagElements[0].content;
    }
    metaTagElements = document.querySelectorAll("meta[property='" + metaTagName + "']") as NodeListOf<HTMLMetaElement>;

    if (metaTagElements.length > 0 && metaTagElements[0].content) {
      return metaTagElements[0].content;
    }

    return undefined;
  }

  private getSousThemes(): string[] | undefined {
    const strSousThemes = this.getAttribute("page.SousTheme", "rc.sousTheme");
    let sousThemes: string[] | undefined = undefined;

    if (strSousThemes) {
      sousThemes = strSousThemes.split(",");
    }
    return sousThemes;
  }
}
